import React, { useState, useEffect, useRef, useCallback } from 'react';
import { groupCallService, GroupCallInvite } from '../services/groupCallService';
import { getAvatarUrl } from '../utils/avatarUtils';

// ============================================================================
// BLE-STYLE BELL RING — Web Audio API
// Classic 3-tone ascending pattern (880→1046→1318 Hz), repeats every 2.5s
// ============================================================================
function createBLERing(ctx: AudioContext): () => void {
  let stopped = false;
  let loopId: ReturnType<typeof setTimeout>;

  const tone = (freq: number, t0: number, dur: number) => {
    if (stopped) return;
    const osc  = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq, t0);
    gain.gain.setValueAtTime(0, t0);
    gain.gain.linearRampToValueAtTime(0.32, t0 + 0.025);
    gain.gain.exponentialRampToValueAtTime(0.001, t0 + dur);
    osc.start(t0);
    osc.stop(t0 + dur);
  };

  const ring = () => {
    if (stopped) return;
    const now = ctx.currentTime;
    tone(880,  now,        0.18);
    tone(1046, now + 0.22, 0.18);
    tone(1318, now + 0.44, 0.24);
    loopId = setTimeout(ring, 2600);
  };
  ring();

  return () => {
    stopped = true;
    clearTimeout(loopId);
  };
}

interface IncomingCallNotificationProps {
  userId: string;
  onAcceptCall: (invite: GroupCallInvite) => void;
}

const IncomingCallNotification: React.FC<IncomingCallNotificationProps> = ({
  userId,
  onAcceptCall,
}) => {
  const [pendingInvites, setPendingInvites] = useState<GroupCallInvite[]>([]);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const stopRingRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!userId) return;
    groupCallService.cleanupStaleInvites(userId);
    const unsub = groupCallService.listenForInvites(userId, setPendingInvites);
    return () => unsub();
  }, [userId]);

  // Start/stop ring sound when invites change
  useEffect(() => {
    if (pendingInvites.length > 0) {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      }
      if (audioCtxRef.current.state === 'suspended') audioCtxRef.current.resume();
      stopRingRef.current?.();
      stopRingRef.current = createBLERing(audioCtxRef.current);
    } else {
      stopRingRef.current?.();
      stopRingRef.current = null;
    }
    return () => { stopRingRef.current?.(); stopRingRef.current = null; };
  }, [pendingInvites.length]);

  const handleAccept = useCallback(async (invite: GroupCallInvite) => {
    stopRingRef.current?.(); stopRingRef.current = null;
    try {
      await groupCallService.acceptInvite(invite.id);
      await groupCallService.joinGroupCall(invite.groupCallId, userId);
      setPendingInvites(p => p.filter(i => i.id !== invite.id));
      onAcceptCall(invite);
    } catch (e) { console.error('Error accepting call:', e); }
  }, [userId, onAcceptCall]);

  const handleDecline = useCallback(async (invite: GroupCallInvite) => {
    try {
      await groupCallService.declineInvite(invite.id);
      setPendingInvites(p => p.filter(i => i.id !== invite.id));
    } catch (e) { console.error('Error declining call:', e); }
  }, []);

  if (pendingInvites.length === 0) return null;

  return (
    <>
      {pendingInvites.map((invite) => {
        const isVideo = invite.callType === 'video';
        const accentColor = isVideo ? '#2563EB' : '#EA580C';

        return (
          <div
            key={invite.id}
            className="sk-call-overlay"
          >
            {/* ── CARD ────────────────────────────────────────────────── */}
            <div className="sk-call-card">

              {/* Animated accent bar at top */}
              <div className="sk-call-bar" style={{ background: accentColor }} />

              {/* Avatar section */}
              <div className="sk-call-avatar-section">
                {/*
                  Ring structure — three expanding rings behind the avatar:
                  ring-3 (outermost, slowest) → ring-2 → ring-1 (innermost)
                  Each is a sibling, NOT a child of the image.
                */}
                <div className="sk-avatar-wrapper">
                  <span className="sk-ring sk-ring-3" style={{ borderColor: accentColor }} />
                  <span className="sk-ring sk-ring-2" style={{ borderColor: accentColor }} />
                  <span className="sk-ring sk-ring-1" style={{ borderColor: accentColor }} />
                  <img
                    src={getAvatarUrl(
                      invite.invitedByUserPhoto || '',
                      invite.invitedByUserId,
                      invite.invitedByUserName,
                      '4F6660'
                    )}
                    alt={invite.invitedByUserName}
                    className="sk-avatar-img"
                    style={{ borderColor: accentColor }}
                  />
                </div>

                {/* Labels */}
                <p className="sk-call-type" style={{ color: accentColor }}>
                  {isVideo ? 'Inkomend videogesprek' : 'Inkomend audiogesprek'}
                </p>
                <h2 className="sk-caller-name">{invite.invitedByUserName}</h2>
                <p className="sk-caller-sub">nodigt je uit</p>

                {/* Group participants */}
                {invite.participantNames.length > 2 && (
                  <div className="sk-participants">
                    <span className="sk-participants-label">Deelnemers</span>
                    <span className="sk-participants-names">
                      {invite.participantNames.join(' · ')}
                    </span>
                  </div>
                )}
              </div>

              {/* ── ACTION BUTTONS ───────────────────────────────────── */}
              <div className="sk-call-actions">

                {/* DECLINE — red, phone rotated down */}
                <button
                  onClick={() => handleDecline(invite)}
                  className="sk-btn-decline"
                  aria-label="Gesprek weigeren"
                >
                  <span className="sk-btn-circle sk-btn-circle--red">
                    {/* Phone rotated 135° = hanging up, universally understood */}
                    <svg
                      width="26" height="26" viewBox="0 0 24 24"
                      fill="none" stroke="white" strokeWidth="2"
                      strokeLinecap="round" strokeLinejoin="round"
                      style={{ transform: 'rotate(135deg)' }}
                    >
                      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.72 19.79 19.79 0 01.12 1.14 2 2 0 012.11 0h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L6.91 7.91a16 16 0 006.08 6.08l1.27-.44a2 2 0 012.11.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" />
                    </svg>
                  </span>
                  <span className="sk-btn-label sk-btn-label--red">Weigeren</span>
                </button>

                {/* ACCEPT — green (audio) or blue (video), phone upright */}
                <button
                  onClick={() => handleAccept(invite)}
                  className="sk-btn-accept"
                  aria-label="Gesprek opnemen"
                >
                  <span
                    className="sk-btn-circle sk-btn-circle--accept"
                    style={{ background: isVideo ? '#2563EB' : '#16A34A' }}
                  >
                    {isVideo ? (
                      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polygon points="23 7 16 12 23 17 23 7" />
                        <rect x="1" y="5" width="15" height="14" rx="2" />
                      </svg>
                    ) : (
                      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.72 19.79 19.79 0 01.12 1.14 2 2 0 012.11 0h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L6.91 7.91a16 16 0 006.08 6.08l1.27-.44a2 2 0 012.11.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" />
                      </svg>
                    )}
                  </span>
                  <span className="sk-btn-label sk-btn-label--accept"
                        style={{ color: isVideo ? '#2563EB' : '#16A34A' }}>
                    Opnemen
                  </span>
                </button>
              </div>
            </div>
          </div>
        );
      })}

      <style>{`
        /* ── Overlay ──────────────────────────────────────────────── */
        .sk-call-overlay {
          position: fixed;
          inset: 0;
          z-index: 9999;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          background: rgba(0, 0, 0, 0.52);
          backdrop-filter: blur(6px);
          -webkit-backdrop-filter: blur(6px);
        }

        /* ── Card ─────────────────────────────────────────────────── */
        .sk-call-card {
          background: #ffffff;
          border-radius: 28px;
          width: 100%;
          max-width: 320px;
          overflow: hidden;
          box-shadow: 0 32px 80px rgba(0,0,0,0.28), 0 0 0 1px rgba(0,0,0,0.06);
          animation: sk-card-in 0.32s cubic-bezier(0.34, 1.52, 0.64, 1) forwards;
        }

        @keyframes sk-card-in {
          from { transform: scale(0.82) translateY(24px); opacity: 0; }
          to   { transform: scale(1)    translateY(0);    opacity: 1; }
        }

        /* ── Accent bar ───────────────────────────────────────────── */
        .sk-call-bar {
          height: 3px;
          animation: sk-bar-blink 1.1s ease-in-out infinite;
        }
        @keyframes sk-bar-blink {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.3; }
        }

        /* ── Avatar section ───────────────────────────────────────── */
        .sk-call-avatar-section {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          padding: 32px 24px 20px;
        }

        /* ── Avatar wrapper + rings ───────────────────────────────── */
        .sk-avatar-wrapper {
          position: relative;
          width: 88px;
          height: 88px;
          margin-bottom: 18px;
        }

        .sk-avatar-img {
          position: relative;
          z-index: 2;
          width: 88px;
          height: 88px;
          border-radius: 50%;
          object-fit: cover;
          border: 3px solid;
          box-shadow: 0 4px 20px rgba(0,0,0,0.14);
        }

        /* Rings sit BEHIND the avatar using z-index */
        .sk-ring {
          position: absolute;
          border-radius: 50%;
          border: 2px solid;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%) scale(0);
          opacity: 0;
        }

        /* Ring 1: tightest, fastest */
        .sk-ring-1 {
          width: 108px;
          height: 108px;
          animation: sk-ring-wave 1.8s ease-out infinite 0s;
        }
        /* Ring 2: medium */
        .sk-ring-2 {
          width: 130px;
          height: 130px;
          animation: sk-ring-wave 1.8s ease-out infinite 0.45s;
        }
        /* Ring 3: outermost, slowest */
        .sk-ring-3 {
          width: 152px;
          height: 152px;
          animation: sk-ring-wave 1.8s ease-out infinite 0.9s;
        }

        @keyframes sk-ring-wave {
          0%   { transform: translate(-50%, -50%) scale(0.6); opacity: 0.7; }
          100% { transform: translate(-50%, -50%) scale(1);   opacity: 0;   }
        }

        /* ── Labels ───────────────────────────────────────────────── */
        .sk-call-type {
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          margin-bottom: 4px;
        }
        .sk-caller-name {
          font-size: 22px;
          font-weight: 700;
          color: #111827;
          margin: 0 0 4px;
          line-height: 1.2;
        }
        .sk-caller-sub {
          font-size: 13px;
          color: #9CA3AF;
          margin: 0;
        }

        /* ── Participants ─────────────────────────────────────────── */
        .sk-participants {
          margin-top: 12px;
          background: #F9FAFB;
          border: 1px solid #F3F4F6;
          border-radius: 12px;
          padding: 8px 14px;
          width: 100%;
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        .sk-participants-label {
          font-size: 10px;
          color: #9CA3AF;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.08em;
        }
        .sk-participants-names {
          font-size: 12px;
          color: #6B7280;
          font-weight: 500;
          line-height: 1.4;
        }

        /* ── Action buttons ───────────────────────────────────────── */
        .sk-call-actions {
          display: flex;
          justify-content: center;
          gap: 48px;
          padding: 0 24px 32px;
        }
        .sk-btn-decline,
        .sk-btn-accept {
          background: none;
          border: none;
          padding: 0;
          cursor: pointer;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          transition: opacity 0.15s;
        }
        .sk-btn-decline:hover,
        .sk-btn-accept:hover { opacity: 0.82; }
        .sk-btn-decline:active,
        .sk-btn-accept:active { transform: scale(0.94); }

        .sk-btn-circle {
          width: 64px;
          height: 64px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 16px rgba(0,0,0,0.18);
          transition: transform 0.12s, box-shadow 0.12s;
        }
        .sk-btn-circle--red {
          background: #EF4444;
          box-shadow: 0 4px 16px rgba(239,68,68,0.4);
        }
        .sk-btn-circle--accept {
          box-shadow: 0 4px 16px rgba(22,163,74,0.4);
        }

        .sk-btn-label {
          font-size: 12px;
          font-weight: 600;
          letter-spacing: 0.01em;
        }
        .sk-btn-label--red   { color: #EF4444; }
      `}</style>
    </>
  );
};

export default IncomingCallNotification;
